import { createContext, useCallback, useContext, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import { useCollection } from '../hooks/useCollection';
import { containersPath, stocksPath } from '../lib/paths';
import { ACTIVE_STATUSES, STATUS } from '../constants/containers';
import { calculateLiveStorage, computeExemptContainerIds } from '../lib/billing';

// Jedno źródło danych depotu dla całej aplikacji.
//
// POWÓD ISTNIENIA: pierwotnie Dashboard, ContainerManagement i StockManagement
// subskrybowały te same dwie kolekcje niezależnie — sześć listenerów na dwie
// kolekcje, czyli potrójny rachunek za odczyty i potrójna praca przy każdej
// zmianie danych.
//
// ZAKRES WCZYTYWANYCH DANYCH: aplikacja NIE wczytuje już całej kolekcji
// kontenerów. Archiwum rośnie w nieskończoność (kontener wydany trzy lata temu
// zostaje w bazie na zawsze), więc wczytywanie wszystkiego przy każdym wejściu
// miało sufit w okolicach kilku tysięcy dokumentów — potem robi się wolno
// i kosztownie, bo Firestore nalicza każdy odczyt dokumentu.
//
// Na stałe subskrybowane jest więc tylko to, co potrzebne do bieżącej pracy:
//   1. kontenery aktywne (dowolny status poza OUT OF STOCK),
//   2. kontenery wydane w ostatnich RECENT_ARCHIVE_DAYS dniach — potrzebne
//      pulpitowi, bo wykres ruchu i liczniki miesięczne obejmują też wydania.
//
// Starsze archiwum, kosz i dane do raportów za dawne miesiące pobierane są
// jednorazowo na żądanie przez fetchContainers().

const DepotDataContext = createContext(null);

// Okno świeżego archiwum. Musi bezpiecznie pokrywać najdłuższy zakres, jakiego
// używa pulpit (wykres 14-dniowy oraz liczniki od początku bieżącego miesiąca).
const RECENT_ARCHIVE_DAYS = 90;

const recentArchiveCutoff = () => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - RECENT_ARCHIVE_DAYS);
  cutoff.setHours(0, 0, 0, 0);
  return cutoff.toISOString();
};

export function DepotDataProvider({ children }) {
  const { userId } = useAuth();
  const path = userId ? containersPath(userId) : null;

  // Data odcięcia liczona raz na sesję. Gdyby powstawała przy każdym renderze,
  // zmieniałaby się co milisekundę i bez przerwy przepinała subskrypcję.
  const cutoff = useMemo(recentArchiveCutoff, []);

  const activeQuery = useCollection(path, {
    constraints: [where('containerStatus', 'in', ACTIVE_STATUSES)],
    key: 'active',
  });

  const recentArchiveQuery = useCollection(path, {
    // Tylko kontenery wydane mają gateOutDate, więc sam zakres daty wystarczy
    // do wyodrębnienia archiwum — bez dokładania warunku na status, który
    // wymagałby indeksu złożonego.
    constraints: [where('gateOutDate', '>=', cutoff)],
    key: `recent-${cutoff}`,
  });

  const stocksQuery = useCollection(userId ? stocksPath(userId) : null);

  const stocks = useMemo(
    () => stocksQuery.data.filter((stock) => !stock.deletedAt),
    [stocksQuery.data]
  );

  const deletedStocks = useMemo(
    () => stocksQuery.data.filter((stock) => stock.deletedAt),
    [stocksQuery.data]
  );

  /** Jednorazowe pobranie kontenerów spoza stale subskrybowanego zakresu. */
  const fetchContainers = useCallback(
    async (...constraints) => {
      if (!path) return [];
      const snapshot = await getDocs(query(collection(db, path), ...constraints));
      return snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
    },
    [path]
  );

  /** Kontenery w koszu — wczytywane dopiero po otwarciu widoku. */
  const fetchTrashedContainers = useCallback(
    () => fetchContainers(where('deletedAt', '!=', null)),
    [fetchContainers]
  );

  /**
   * Wszystkie kontenery jednego stocku — z całej historii, nie tylko z zakresu
   * trzymanego w pamięci. Potrzebne raportowi „pełny wykaz stocku", który
   * z definicji ma obejmować również dawne wydania.
   */
  const fetchContainersForStock = useCallback(
    async (stockId) => {
      const rows = await fetchContainers(where('associatedStockId', '==', stockId));
      return rows.filter((container) => !container.deletedAt);
    },
    [fetchContainers]
  );

  /** Archiwum z wybranego zakresu dat (klucze "YYYY-MM-DD"). */
  const fetchArchive = useCallback(
    (fromKey, toKey) =>
      fetchContainers(
        where('gateOutDate', '>=', `${fromKey}T00:00:00.000Z`),
        where('gateOutDate', '<=', `${toKey}T23:59:59.999Z`)
      ),
    [fetchContainers]
  );

  /**
   * Komplet kontenerów istotnych dla rozliczenia danego miesiąca.
   *
   * Nie wystarczy wziąć wydań z tego miesiąca: kontener przyjęty w styczniu
   * i wydany w czerwcu zajmował plac również w marcu, więc do marcowego
   * rozliczenia musi trafić. Warunkiem jest zatem „wydany nie wcześniej niż
   * z początkiem rozliczanego miesiąca" — plus wszystkie kontenery wciąż
   * na placu, które są już subskrybowane na stałe.
   */
  const fetchContainersForMonth = useCallback(
    async (monthString) => {
      const [year, month] = monthString.split('-').map(Number);
      const monthStart = new Date(Date.UTC(year, month - 1, 1)).toISOString();

      const gatedOutSince = await fetchContainers(where('gateOutDate', '>=', monthStart));

      // Deduplikacja: kontenery wydane niedawno są i w subskrypcji, i w wyniku
      // zapytania. Bez tego trafiłyby do rozliczenia dwukrotnie.
      const byId = new Map();
      [...activeQuery.data, ...gatedOutSince].forEach((container) => {
        if (!container.deletedAt) byId.set(container.id, container);
      });

      return [...byId.values()];
    },
    [fetchContainers, activeQuery.data]
  );

  const activeContainers = useMemo(
    () => activeQuery.data.filter((container) => !container.deletedAt),
    [activeQuery.data]
  );

  const recentArchivedContainers = useMemo(
    () =>
      recentArchiveQuery.data.filter(
        (container) => !container.deletedAt && container.containerStatus === STATUS.OUT_OF_STOCK
      ),
    [recentArchiveQuery.data]
  );

  /** Wszystko, co jest w pamięci: aktywne + świeże archiwum. */
  const containers = useMemo(
    () => [...activeContainers, ...recentArchivedContainers],
    [activeContainers, recentArchivedContainers]
  );

  const stocksById = useMemo(
    () => Object.fromEntries(stocksQuery.data.map((stock) => [stock.id, stock])),
    [stocksQuery.data]
  );

  const getStockName = useCallback(
    (stockId) => stocksById[stockId]?.stockName || 'Nieznany',
    [stocksById]
  );

  /** Statystyki per stock — liczone z kontenerów aktywnych. */
  const countsByStock = useMemo(() => {
    const counts = {};

    activeContainers.forEach((container) => {
      const stockId = container.associatedStockId;
      if (!stockId) return;

      if (!counts[stockId]) {
        counts[stockId] = {
          active: 0,
          [STATUS.GATE_IN_ADVISED]: 0,
          [STATUS.ON_STOCK]: 0,
          [STATUS.GATE_OUT_ADVISED]: 0,
        };
      }

      counts[stockId].active += 1;
      if (counts[stockId][container.containerStatus] !== undefined) {
        counts[stockId][container.containerStatus] += 1;
      }
    });

    return counts;
  }, [activeContainers]);

  /**
   * Kontenery objęte limitem "free containers", policzone raz per stock.
   * Bez tego każde wywołanie kosztu na żywo sortowałoby całą listę kontenerów
   * danego stocku od nowa — przy renderze tabeli byłyby to setki sortowań.
   */
  const exemptIdsByStock = useMemo(() => {
    const result = {};
    stocks.forEach((stock) => {
      const quota = stock.rates?.freeContainers || 0;
      if (quota <= 0) {
        result[stock.id] = new Set();
        return;
      }
      result[stock.id] = computeExemptContainerIds(
        containers.filter((c) => c.associatedStockId === stock.id),
        quota
      );
    });
    return result;
  }, [stocks, containers]);

  const getLiveStorage = useCallback(
    (container) => {
      const stock = stocksById[container.associatedStockId];
      if (!stock) return null;
      const isExempt = exemptIdsByStock[stock.id]?.has(container.id) || false;
      return calculateLiveStorage(container, stock, { isExempt });
    },
    [stocksById, exemptIdsByStock]
  );

  const value = useMemo(
    () => ({
      containers,
      activeContainers,
      recentArchivedContainers,
      stocks,
      deletedStocks,
      stocksById,
      countsByStock,
      getStockName,
      getLiveStorage,
      fetchContainers,
      fetchContainersForStock,
      fetchArchive,
      fetchTrashedContainers,
      fetchContainersForMonth,
      recentArchiveDays: RECENT_ARCHIVE_DAYS,
      loading: activeQuery.loading || stocksQuery.loading,
      error: activeQuery.error || recentArchiveQuery.error || stocksQuery.error,
    }),
    [
      containers,
      activeContainers,
      recentArchivedContainers,
      stocks,
      deletedStocks,
      stocksById,
      countsByStock,
      getStockName,
      getLiveStorage,
      fetchContainers,
      fetchContainersForStock,
      fetchArchive,
      fetchTrashedContainers,
      fetchContainersForMonth,
      activeQuery.loading,
      activeQuery.error,
      recentArchiveQuery.error,
      stocksQuery.loading,
      stocksQuery.error,
    ]
  );

  return <DepotDataContext.Provider value={value}>{children}</DepotDataContext.Provider>;
}

export function useDepotData() {
  const context = useContext(DepotDataContext);
  if (!context) throw new Error('useDepotData musi być użyty wewnątrz <DepotDataProvider>');
  return context;
}
